import React, { useState, useEffect } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'

import { getCurrentUser, getAuthHeaders } from '../../services/authService'
import { useSubscription } from '../../context/SubscriptionContext'
import { invitationDismissed } from '../TeamInvitationModal'

function PaymentModal({
	isOpen,
	onClose,
	isDarkMode,
	initialBillingCycle = 'monthly',
}) {
	const user = getCurrentUser()
	const { subscriptionData } = useSubscription()

	const [invoiceData, setInvoiceData] = useState(null)
	const [isLoading, setIsLoading] = useState(false)
	const [loading, setLoading] = useState(true)
	const [teamSeats, setTeamSeats] = useState(2)
	const [teamMembership, setTeamMembership] = useState(null)
	const [showLeaveTeamConfirm, setShowLeaveTeamConfirm] = useState(false)
	const [pendingPlanUpgrade, setPendingPlanUpgrade] = useState(null)
	const [isYearly, setIsYearly] = useState(initialBillingCycle === 'yearly')

	const getUserBillingInfo = (invoiceDataParam = invoiceData) => {
		const subscriptionType =
			invoiceDataParam?.subscription?.type || user?.subscription?.type || 'free'

		const userIsYearly = subscriptionType.includes('yearly')

		let planName = 'Free'
		if (subscriptionType.includes('pro_plan')) {
			planName = 'Pro'
		} else if (subscriptionType.includes('plus_plan')) {
			planName = 'Plus'
		} else if (subscriptionType.includes('team_plan')) {
			planName = 'Team'
		}

		return { userIsYearly, planName, subscriptionType }
	}

	const [billingInfo, setBillingInfo] = useState(() => getUserBillingInfo(null))

	const getCurrentPlan = () => {
		if (billingInfo.planName === 'Pro') {
			return { name: 'Pro', color: 'bg-blue-500', textColor: 'text-blue-600' }
		} else if (billingInfo.planName === 'Plus') {
			return {
				name: 'Plus',
				color: 'bg-green-500',
				textColor: 'text-green-600',
			}
		} else if (billingInfo.planName === 'Team') {
			return {
				name: 'Team',
				color: 'bg-purple-500',
				textColor: 'text-purple-600',
			}
		} else {
			return { name: 'Free', color: 'bg-gray-500', textColor: 'text-gray-600' }
		}
	}

	const currentPlan = getCurrentPlan()

	const isCurrentPlanAndCycle = planName => {
		if (currentPlan.name !== planName) return false
		if (currentPlan.name === 'Free') return true

		const currentIsYearly = billingInfo.subscriptionType.includes('yearly')
		return currentIsYearly === isYearly
	}

	React.useEffect(() => {
		if (invoiceData?.subscription?.type) {
			const newBillingInfo = getUserBillingInfo(invoiceData)
			setBillingInfo(newBillingInfo)
			setIsYearly(newBillingInfo.userIsYearly)
		}
	}, [invoiceData])

	const handleLeaveTeam = async () => {
		try {
			const response = await fetch('/api/team/leave-for-upgrade', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: localStorage.getItem('jwtToken'),
				},
			})

			if (!response.ok) {
				throw new Error('Failed to leave team')
			}

			setShowLeaveTeamConfirm(false)
			setTeamMembership({ isMember: false, isOwner: false })

			// Proceed with the upgrade
			if (pendingPlanUpgrade) {
				handleSubscription(pendingPlanUpgrade, true)
			}
		} catch (error) {
			console.error('Error leaving team:', error)
			alert('Failed to leave team. Please try again.')
		}
	}

	const handleSubscription = async (planName, skipTeamCheck = false) => {
		// Check if user is a team member (not owner) trying to upgrade
		if (
			!skipTeamCheck &&
			teamMembership?.isMember &&
			!teamMembership?.isOwner &&
			currentPlan.name === 'Free'
		) {
			setPendingPlanUpgrade(planName)
			setShowLeaveTeamConfirm(true)
			return
		}

		setIsLoading(true)

		const planType = isYearly ? 'yearly' : 'monthly'

		try {
			if (currentPlan.name !== 'Free') {
				const returnUrl = window.location.pathname.includes('/loadboard')
					? `${window.location.origin}/loadboard`
					: window.location.href

				let queryString = `/api/stripe/customer-portal-plans?targetPlan=${planName}&targetCycle=${planType}&returnUrl=${encodeURIComponent(
					returnUrl,
				)}`

				if (planName === 'team') {
					queryString += `&seats=${teamSeats}`
				}

				const response = await fetch(queryString, {
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						Authorization: localStorage.getItem('jwtToken'),
					},
				})

				if (!response.ok) {
					throw new Error('Failed to get customer portal URL')
				}

				const portalUrl = await response.text()
				window.location.href = portalUrl
			} else {
				// No subscription - create new checkout session
				const cancelUrl = window.location.pathname.includes('/loadboard')
					? `${window.location.origin}/loadboard`
					: window.location.href

				const response = await fetch('/api/stripe/create-checkout-session', {
					method: 'POST',
					headers: getAuthHeaders(),
					body: JSON.stringify({
						planType,
						planName,
						customerEmail: user?.email,
						cancelUrl: cancelUrl,
						...(planName === 'team' && { seats: teamSeats }),
					}),
				})

				if (!response.ok) throw new Error('Network response was not ok.')

				const session = await response.json()
				window.location.href = session?.url
			}

			setIsLoading(false)
		} catch (error) {
			console.error('Error during subscription process:', error)
			setIsLoading(false)
		}
	}

	useEffect(() => {
		if (!isOpen) return

		if (subscriptionData) {
			setInvoiceData(subscriptionData)
			setLoading(false)
		}

		// Check if user is a team member
		const checkTeamMembership = async () => {
			try {
				const response = await fetch('/api/team/check-membership', {
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						Authorization: localStorage.getItem('jwtToken'),
					},
				})

				if (response.ok) {
					const data = await response.json()
					setTeamMembership(data)
				}
			} catch (error) {
				console.error('Error checking team membership:', error)
			}
		}

		checkTeamMembership()
	}, [isOpen, subscriptionData])

	// Don't show payment modal if there are pending invitations and user hasn't dismissed yet
	const hasPendingInvitations =
		subscriptionData?.pendingInvitations &&
		subscriptionData.pendingInvitations.length > 0 &&
		!invitationDismissed

	if (!isOpen || hasPendingInvitations) return null

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center">
			{/* Backdrop */}
			<div className="absolute inset-0 bg-black bg-opacity-50"></div>

			{/* Modal Content */}
			<div
				className={`relative w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${
					isDarkMode ? 'bg-gray-800' : 'bg-white'
				}`}
			>
				{/* Header */}
				<div className="flex items-center justify-end p-2">
					<button
						onClick={onClose}
						className={`p-1 rounded-full transition-colors ${
							isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
						}`}
					>
						<XMarkIcon
							className={`h-5 w-5 ${
								isDarkMode ? 'text-gray-400' : 'text-gray-500'
							}`}
						/>
					</button>
				</div>

				{/* Content */}
				<div className="px-6 pb-4">
					{loading ? (
						<div className="animate-pulse">
							{/* Plan Cards Skeleton */}
							<div className="grid md:grid-cols-3 gap-6 mb-6 mt-12">
								<div
									className={`p-6 rounded-lg ${
										isDarkMode
											? 'bg-gray-700 border border-gray-600'
											: 'bg-gray-50 border border-gray-200'
									}`}
								>
									<div
										className={`h-6 rounded w-16 mx-auto mb-4 ${
											isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
										}`}
									></div>
									<div
										className={`h-8 rounded w-24 mx-auto mb-2 ${
											isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
										}`}
									></div>
									<div className="space-y-2 mt-6">
										{[1, 2, 3, 4, 5, 6, 7].map(i => (
											<div key={i} className="flex items-center">
												<div
													className={`w-4 h-4 rounded mr-2 ${
														isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
													}`}
												></div>
												<div
													className={`h-3 rounded flex-1 ${
														isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
													}`}
												></div>
											</div>
										))}
									</div>
								</div>
								<div
									className={`p-6 rounded-lg ${
										isDarkMode
											? 'bg-gray-700 border border-gray-600'
											: 'bg-gray-50 border border-gray-200'
									}`}
								>
									<div
										className={`h-6 rounded w-12 mx-auto mb-4 ${
											isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
										}`}
									></div>
									<div
										className={`h-8 rounded w-24 mx-auto mb-2 ${
											isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
										}`}
									></div>
									<div className="space-y-2 mt-6">
										{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(i => (
											<div key={i} className="flex items-center">
												<div
													className={`w-4 h-4 rounded mr-2 ${
														isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
													}`}
												></div>
												<div
													className={`h-3 rounded flex-1 ${
														isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
													}`}
												></div>
											</div>
										))}
									</div>
								</div>
								<div
									className={`p-6 rounded-lg ${
										isDarkMode
											? 'bg-gray-700 border border-gray-600'
											: 'bg-gray-50 border border-gray-200'
									}`}
								>
									<div
										className={`h-6 rounded w-12 mx-auto mb-4 ${
											isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
										}`}
									></div>
									<div
										className={`h-8 rounded w-24 mx-auto mb-2 ${
											isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
										}`}
									></div>
									<div className="space-y-2 mt-6">
										{[1, 2, 3, 4, 5, 6].map(i => (
											<div key={i} className="flex items-center">
												<div
													className={`w-4 h-4 rounded mr-2 ${
														isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
													}`}
												></div>
												<div
													className={`h-3 rounded flex-1 ${
														isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
													}`}
												></div>
											</div>
										))}
									</div>
								</div>
							</div>
						</div>
					) : (
						<>
							{/* Load Statistics Message for Free Plan */}
							{currentPlan.name === 'Free' && (
								<p
									className={`text-sm text-center mb-2 ${
										isDarkMode ? 'text-gray-400' : 'text-gray-600'
									}`}
								>
									Your plan does not include website statistics.{' '}
									<a
										href="https://relay.amazon.com/loadboard/search"
										target="_blank"
										rel="noopener noreferrer"
										className={`font-medium transition-colors ${
											isDarkMode
												? 'text-blue-400 hover:text-blue-300'
												: 'text-blue-600 hover:text-blue-700'
										}`}
									>
										Go with free plan
									</a>
								</p>
							)}

							{/* Billing Toggle - Rounded Tab */}
							<div className="flex items-center justify-center mb-4">
								<div
									className={`p-1 rounded-full flex w-[270px] shadow-sm ${
										isDarkMode
											? 'bg-gray-700 border border-gray-600'
											: 'bg-white border border-gray-200'
									}`}
								>
									<button
										onClick={() => setIsYearly(false)}
										className={`flex-1 px-3 py-2 rounded-full text-sm font-medium transition-all ${
											!isYearly
												? isDarkMode
													? 'bg-gray-600 text-white shadow-sm'
													: 'bg-gray-200 text-gray-900 shadow-sm'
												: isDarkMode
													? 'text-gray-300 hover:text-white'
													: 'text-gray-600 hover:text-gray-900'
										}`}
									>
										Monthly
									</button>
									<button
										onClick={() => setIsYearly(true)}
										className={`flex-1 px-3 py-2 rounded-full text-sm font-medium transition-all ${
											isYearly
												? isDarkMode
													? 'bg-gray-600 text-white shadow-sm'
													: 'bg-gray-200 text-gray-900 shadow-sm'
												: isDarkMode
													? 'text-gray-300 hover:text-white'
													: 'text-gray-600 hover:text-gray-900'
										}`}
									>
										Yearly
										<span
											className={`text-[10px] rounded-full px-2 py-0.5 ml-1 ${isDarkMode ? 'text-green-400 bg-green-900/50' : 'text-green-700 bg-green-100'}`}
										>
											-32%
										</span>
									</button>
								</div>
							</div>

							{/* Plan Cards */}
							<div className="grid gap-4 mb-6 md:grid-cols-3">
								{/* Plus Plan */}
								<div
									className={`p-6 rounded-lg transition-colors relative flex flex-col ${
										isCurrentPlanAndCycle('Plus')
											? isDarkMode
												? 'border border-gray-600 bg-gray-700 opacity-75'
												: 'border border-gray-200 bg-gray-50 opacity-75'
											: isDarkMode
												? 'border border-gray-600 bg-gray-700'
												: 'border border-gray-200 bg-white'
									}`}
								>
									<div className="mb-4">
										<div
											className={`font-bold text-xl ${
												isDarkMode ? 'text-white' : 'text-gray-900'
											}`}
										>
											Plus
										</div>
										<div
											className={`text-3xl font-bold mt-2 ${
												isDarkMode ? 'text-white' : 'text-gray-900'
											}`}
										>
											{isYearly ? '$196' : '$24'}
											<span
												className={`text-sm font-medium ml-1 ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												/{isYearly ? 'year' : 'month'}
											</span>
										</div>
										{isYearly && (
											<div className="text-sm text-green-600 font-medium">
												Save $92 (-32%)
											</div>
										)}
										<p
											className={`text-sm mt-2 mb-3 ${
												isDarkMode ? 'text-gray-400' : 'text-gray-600'
											}`}
										>
											For growing operations.
										</p>
									</div>

									<div className="space-y-2">
										<p
											className={`text-sm font-medium mb-2 ${
												isDarkMode ? 'text-gray-300' : 'text-gray-600'
											}`}
										>
											Everything in Free and:
										</p>
										<ul className="space-y-1 text-sm">
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>Extension 5 devices/browsers</span>
											</li>
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>Web app 1 device/browser</span>
											</li>
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>Financials & driver earnings</span>
											</li>
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>In-Page trip notifications</span>
											</li>
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>Load analytics & backhaul</span>
											</li>
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>Telegram notifications</span>
											</li>
										</ul>
									</div>

									{/* Plus Plan Button */}
									<div className="mt-auto pt-4">
										{isCurrentPlanAndCycle('Plus') ? (
											<button
												disabled
												className={`w-full py-2 px-4 rounded-lg font-medium cursor-not-allowed ${
													isDarkMode
														? 'bg-gray-600 text-gray-400'
														: 'bg-gray-300 text-gray-500'
												}`}
											>
												Current Plan
											</button>
										) : currentPlan.name === 'Plus' ? (
											billingInfo.userIsYearly && !isYearly ? (
												<button
													disabled
													className={`w-full py-2 px-4 rounded-lg font-medium cursor-not-allowed ${
														isDarkMode
															? 'bg-gray-600 text-gray-400'
															: 'bg-gray-300 text-gray-500'
													}`}
												>
													Downgrade Not Available
												</button>
											) : (
												<button
													onClick={() => handleSubscription('plus')}
													disabled={isLoading}
													className="w-full py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
												>
													{isLoading ? 'Processing...' : 'Switch to Yearly'}
												</button>
											)
										) : currentPlan.name === 'Free' ? (
											<button
												onClick={() => handleSubscription('plus')}
												disabled={isLoading}
												className="w-full py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
											>
												{isLoading ? 'Processing...' : 'Upgrade to Plus'}
											</button>
										) : (
											<button
												disabled
												className="w-full py-2 px-4 bg-gray-300 text-gray-500 rounded-lg font-medium cursor-not-allowed"
											>
												Downgrade Not Available
											</button>
										)}
									</div>
								</div>

								{/* Pro Plan */}
								<div
									className={`p-6 rounded-lg transition-colors relative flex flex-col ${
										isCurrentPlanAndCycle('Pro')
											? isDarkMode
												? 'border border-gray-600 bg-gray-700 opacity-75'
												: 'border border-gray-200 bg-gray-50 opacity-75'
											: isDarkMode
												? 'border border-gray-600 bg-gray-700'
												: 'border border-gray-200 bg-white'
									}`}
								>
									{!isCurrentPlanAndCycle('Pro') && (
										<div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
											<span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-medium">
												Popular
											</span>
										</div>
									)}

									<div className="mb-4">
										<div
											className={`font-bold text-xl ${
												isDarkMode ? 'text-white' : 'text-gray-900'
											}`}
										>
											Pro
										</div>
										<div
											className={`text-3xl font-bold mt-2 ${
												isDarkMode ? 'text-white' : 'text-gray-900'
											}`}
										>
											{isYearly ? '$399' : '$49'}
											<span
												className={`text-sm font-medium ml-1 ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												/{isYearly ? 'year' : 'month'}
											</span>
										</div>
										{isYearly && (
											<div className="text-sm text-green-600 font-medium">
												Save $189 (-32%)
											</div>
										)}
										<p
											className={`text-sm mt-2 mb-3 ${
												isDarkMode ? 'text-gray-400' : 'text-gray-600'
											}`}
										>
											Advanced tools for pros.
										</p>
									</div>

									<div className="space-y-2">
										<p
											className={`text-sm font-medium mb-2 ${
												isDarkMode ? 'text-gray-300' : 'text-gray-600'
											}`}
										>
											Everything in Plus and:
										</p>
										<ul className="space-y-1 text-sm">
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>Extension 20 devices/browsers</span>
											</li>
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>Web app 3 devices/browsers</span>
											</li>
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>Telegram trip notifications</span>
											</li>
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>Extended statistics & insights</span>
											</li>
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>"PAT" creation via website</span>
											</li>
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>Safety tracking (coming soon)</span>
											</li>
										</ul>
									</div>

									{/* Pro Plan Button */}
									<div className="mt-auto pt-4">
										{isCurrentPlanAndCycle('Pro') ? (
											<button
												disabled
												className={`w-full py-2 px-4 rounded-lg font-medium cursor-not-allowed ${
													isDarkMode
														? 'bg-gray-600 text-gray-400'
														: 'bg-gray-300 text-gray-500'
												}`}
											>
												Current Plan
											</button>
										) : currentPlan.name === 'Pro' ? (
											billingInfo.userIsYearly && !isYearly ? (
												<button
													disabled
													className={`w-full py-2 px-4 rounded-lg font-medium cursor-not-allowed ${
														isDarkMode
															? 'bg-gray-600 text-gray-400'
															: 'bg-gray-300 text-gray-500'
													}`}
												>
													Downgrade Not Available
												</button>
											) : (
												<button
													onClick={() => handleSubscription('pro')}
													disabled={isLoading}
													className="w-full py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
												>
													{isLoading ? 'Processing...' : 'Switch to Yearly'}
												</button>
											)
										) : currentPlan.name === 'Free' ||
										  currentPlan.name === 'Plus' ? (
											<button
												onClick={() => handleSubscription('pro')}
												disabled={isLoading}
												className="w-full py-2 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
											>
												{isLoading ? 'Processing...' : 'Upgrade to Pro'}
											</button>
										) : (
											<button
												disabled
												className="w-full py-2 px-4 bg-gray-300 text-gray-500 rounded-lg font-medium cursor-not-allowed"
											>
												Downgrade Not Available
											</button>
										)}
									</div>
								</div>

								{/* Team Plan */}
								<div
									className={`p-6 rounded-lg transition-colors relative flex flex-col ${
										currentPlan.name === 'Team'
											? isDarkMode
												? 'border border-gray-600 bg-gray-700 opacity-75'
												: 'border border-gray-200 bg-gray-50 opacity-75'
											: isDarkMode
												? 'border border-gray-600 bg-gray-700'
												: 'border border-gray-200 bg-white'
									}`}
								>
									<div className="mb-4">
										<div
											className={`font-bold text-xl ${
												isDarkMode ? 'text-white' : 'text-gray-900'
											}`}
										>
											Team
										</div>
										<div className="flex items-baseline gap-2 mt-2">
											<div
												className={`text-3xl font-bold ${
													isDarkMode ? 'text-white' : 'text-gray-900'
												}`}
											>
												${isYearly ? teamSeats * 399 : teamSeats * 45}
												<span
													className={`text-sm font-medium ml-1 ${
														isDarkMode ? 'text-gray-300' : 'text-gray-700'
													}`}
												>
													/{isYearly ? 'year' : 'month'}
												</span>
											</div>
											<div className="text-sm text-gray-500">
												${isYearly ? '399' : '45'}/seat
											</div>
										</div>
										{isYearly && (
											<div className="text-sm text-green-600 font-medium">
												Save ${teamSeats * 141} (-26%)
											</div>
										)}
										{/* Seat Selector */}
										<div className="mt-2 mb-3 flex items-center gap-3">
											<label
												className={`text-sm font-medium ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												Number of seats
											</label>
											<div className="flex items-center gap-2">
												<button
													onClick={() =>
														setTeamSeats(Math.max(2, teamSeats - 1))
													}
													disabled={teamSeats <= 2}
													className={`w-8 h-8 rounded-md flex items-center justify-center text-lg font-bold transition-colors ${
														teamSeats <= 2
															? isDarkMode
																? 'bg-gray-700 text-gray-500 cursor-not-allowed'
																: 'bg-gray-200 text-gray-400 cursor-not-allowed'
															: isDarkMode
																? 'bg-gray-700 text-white hover:bg-gray-600'
																: 'bg-gray-200 text-gray-900 hover:bg-gray-300'
													}`}
												>
													−
												</button>
												<span
													className={`text-xl font-bold w-8 text-center ${
														isDarkMode ? 'text-white' : 'text-gray-900'
													}`}
												>
													{teamSeats}
												</span>
												<button
													onClick={() =>
														setTeamSeats(Math.min(5, teamSeats + 1))
													}
													disabled={teamSeats >= 5}
													className={`w-8 h-8 rounded-md flex items-center justify-center text-lg font-bold transition-colors ${
														teamSeats >= 5
															? isDarkMode
																? 'bg-gray-700 text-gray-500 cursor-not-allowed'
																: 'bg-gray-200 text-gray-400 cursor-not-allowed'
															: isDarkMode
																? 'bg-gray-700 text-white hover:bg-gray-600'
																: 'bg-gray-200 text-gray-900 hover:bg-gray-300'
													}`}
												>
													+
												</button>
											</div>
										</div>
									</div>

									<div className="space-y-2">
										<p
											className={`text-sm font-medium mb-2 ${
												isDarkMode ? 'text-gray-300' : 'text-gray-600'
											}`}
										>
											Everything in Pro and:
										</p>
										<ul className="space-y-1 text-sm">
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>Extension {teamSeats * 20} devices/browsers</span>
											</li>
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>Web app {teamSeats * 3} devices/browsers</span>
											</li>
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>Shared team workspace</span>
											</li>
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>Invite team members</span>
											</li>
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>Centralized billing</span>
											</li>
											<li
												className={`flex items-start ${
													isDarkMode ? 'text-gray-300' : 'text-gray-700'
												}`}
											>
												<span className="text-green-500 mr-1.5 mt-0">✓</span>
												<span>Flexible seat management</span>
											</li>
										</ul>
									</div>

									{/* Team Plan Button */}
									<div className="mt-auto pt-4">
										{currentPlan.name === 'Team' ? (
											<button
												disabled
												className={`w-full py-2 px-4 rounded-lg font-medium cursor-not-allowed ${
													isDarkMode
														? 'bg-gray-600 text-gray-400'
														: 'bg-gray-300 text-gray-500'
												}`}
											>
												Current Plan
											</button>
										) : (
											<button
												onClick={() => handleSubscription('team')}
												disabled={isLoading}
												className="w-full py-2 px-4 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
											>
												{isLoading ? 'Processing...' : 'Upgrade to Team'}
											</button>
										)}
									</div>
								</div>
							</div>
						</>
					)}
				</div>
			</div>

			{/* Leave Team Confirmation Modal */}
			{showLeaveTeamConfirm && (
				<div className="absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-75">
					<div
						className={`w-full max-w-md mx-4 p-6 rounded-lg shadow-xl ${
							isDarkMode ? 'bg-gray-700' : 'bg-white'
						}`}
					>
						<h3
							className={`text-lg font-semibold mb-4 ${
								isDarkMode ? 'text-white' : 'text-gray-900'
							}`}
						>
							Leave Team to Upgrade
						</h3>
						<p
							className={`mb-6 ${
								isDarkMode ? 'text-gray-300' : 'text-gray-600'
							}`}
						>
							You are currently part of{' '}
							<span className="font-semibold">{teamMembership?.teamName}</span>{' '}
							team. To upgrade to your own plan, you need to leave the team
							first.
							<br />
							<br />
							Do you want to continue?
						</p>
						<div className="flex gap-3 justify-end">
							<button
								onClick={() => {
									setShowLeaveTeamConfirm(false)
									setPendingPlanUpgrade(null)
								}}
								className={`px-4 py-2 rounded-md transition-colors ${
									isDarkMode
										? 'bg-gray-600 hover:bg-gray-500 text-white'
										: 'bg-gray-200 hover:bg-gray-300 text-gray-800'
								}`}
							>
								Cancel
							</button>
							<button
								onClick={handleLeaveTeam}
								className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
							>
								Leave Team & Upgrade
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default PaymentModal
